import {IServiceFactory} from "../../services/service-factory.interface";
import {Application, Container, Emitter, Graphics, Text} from "../../services/pixi-app/pixi-app.service.interface";
import {PixiAnimation} from "../../services/pixi-app/pixi-animation"
import {PixiAsset} from "../../services/pixi-app/pixi-assets-manager/asset/pixi-asset";
import {IPixiLibWrapper} from "../../services/pixi-app/pixi-lib-wrapper/pixi-lib-wrapper.interface";
import {PixiSpineResource} from "../../services/pixi-app/resources/pixi-spine-resource";
import {IGsapService} from "../../services/gsap/gsap.service.interface";
import {DESIGN_DIMENSIONS} from "./magic-8-ball-consts";
import {AddDisposeActionFn} from "../../services/pixi-app/pixi-animation.interface";

export class Magic8BallAnimation {
    private pixiAnimation: PixiAnimation | null = null;
    // 'present_prize' is when the prizes are presented before the shake / "mock_prize" is during the shake (when the prizes goes through the center of the ball)
    private prizePresentMode: "present_prize" | "mock_prize" = "present_prize";
    private currentPrizeIndex: number | null = null;
    private objects: {
        prizeWrapper: {
            layer: Container;
            objects: Container[];
        }
        prizesMask: Graphics;
        ball: PixiSpineResource;
        background: PixiSpineResource;
        buttonShakeTheBallWrapper: Container;
        titleTexts: Container;
        prizePresentationText: Text;
        winTitleTextsContainer: Container;
        backToPlayButtonWrapper: Container;
        winInfoTextsContainer: Container;
        bgParticlesEmitter: Emitter;
    } = {
        prizeWrapper: null,
        prizesMask: null,
        ball: null,
        background: null,
        buttonShakeTheBallWrapper: null,
        titleTexts: null,
        prizePresentationText: null,
        winTitleTextsContainer: null,
        backToPlayButtonWrapper: null,
        winInfoTextsContainer: null,
        bgParticlesEmitter: null
    };
    constructor(
        private services: IServiceFactory,
        private gsapService: IGsapService,
        private prizes: string[],
        private prizeWonIndex: number,
        private onShakeStart: undefined | (() => void),
        private onShakeEnd: undefined | (() => void),
        private onPrizeClaim: undefined | (() => void),
        private onResize: undefined | ((scale: number) => void)
    ) {}
    init() {
        this.pixiAnimation = this.services.pixiApp.createPixiAnimation(
            {
                width: DESIGN_DIMENSIONS.WIDTH,
                height: DESIGN_DIMENSIONS.HEIGHT,
                backgroundColor: "0xcccccc"
            },
            {
                'backgroundParticles': this.services.theme.currentTheme.assets.images.magic_8_ball.particles.backgroundParticles_json.toString(),
                // Not used for the moment but might change
                // 'ellipse': this.services.theme.currentTheme.assets.images.magic_8_ball.ellipse_png.toString(),
                'star': this.services.theme.currentTheme.assets.images.magic_8_ball.star_png.toString(),
                'ball': this.services.theme.currentTheme.assets.images.magic_8_ball.spine._8_ball_json.toString(),
                'background': this.services.theme.currentTheme.assets.images.magic_8_ball.spine.bg_json.toString(),
                'blue_layer': this.services.theme.currentTheme.assets.images.magic_8_ball.blue_layer_png.toString(),
                'button': this.services.theme.currentTheme.assets.images.magic_8_ball.spine.button_json.toString(),
                'prize': this.services.theme.currentTheme.assets.images.magic_8_ball.prize_png.toString(),
                'transparentTextureBg': this.services.theme.currentTheme.assets.images.magic_8_ball.text_bg_transparent_png.toString()
                /**
                 * In case we would use a font in the animation that is not used in the Orbit app then we would need to load the font
                 * Like this: "888-Regular": "./fonts/888-Regular.woff"
                 * Beware, if using a font name that starts with a number like in the example above it won't work. The font filename should start with a letter
                 * (I found this by experimenting, haven't found information written anywhere)
                 * So I would recommend instead of renaming the file "888-Regular" to "Triple8-Regular" for example.
                 * Also, if the filename has "-" inside of it, the naming of the fontFamily used when creating text must contain spaces instead of dashes
                 * Example if the filename is "Something-Otherthing-Regular" the fontFamily would be "Something Otherthing Regular"
                 */
            },
            {
                render: ({pixiApp, resources: assets, addDisposeAction, pixiLibWrapper}) => {
                    const rootContainer = this.createRootContainer(pixiLibWrapper);
                    // this.objects.rootContainer = rootContainer;
                    
                    const background = this.createAndShowBackground(assets.background, pixiLibWrapper);
                    rootContainer.addChild(background.spineAnimation as any);
                    this.objects.background = background;

                    const blueLayer = this.createAndShowBlueLayer(assets.blue_layer, pixiLibWrapper);
                    rootContainer.addChild(blueLayer.sprite as any);
                    // this.objects.background = background;

                    const ball = this.createAndShowBall(assets.ball, assets.transparentTextureBg, pixiApp, addDisposeAction, pixiLibWrapper);
                    rootContainer.addChild(ball.spineAnimation as any);
                    this.objects.ball = ball;

                    const prizePresentationText = this.createPrizePresentationText(pixiLibWrapper);
                    rootContainer.addChild(prizePresentationText as any);
                    this.objects.prizePresentationText = prizePresentationText;

                    const titleTexts = this.createTitleTexts(pixiLibWrapper);
                    rootContainer.addChild(titleTexts as any);
                    this.objects.titleTexts = titleTexts;
    
                    const prizesMask = this.createPrizeMask(pixiLibWrapper);
                    rootContainer.addChild(prizesMask as any);
                    this.objects.prizesMask = prizesMask;

                    const prizeWrapper = this.createPrizeLayer(this.prizes, assets.prize, prizesMask, pixiLibWrapper);
                    rootContainer.addChild(prizeWrapper.layer as any);
                    this.objects.prizeWrapper = prizeWrapper;

                    const buttonShakeTheBallWrapper = this.createShakeTheBallButton(assets.button, addDisposeAction, pixiLibWrapper);
                    rootContainer.addChild(buttonShakeTheBallWrapper as any);
                    this.objects.buttonShakeTheBallWrapper = buttonShakeTheBallWrapper;

                    const {particleContainer, emitter} = this.createBackgroundParticles(assets.backgroundParticles, [assets.star], pixiLibWrapper);
                    rootContainer.addChild(particleContainer as any);
                    this.objects.bgParticlesEmitter = emitter;

                    /**
                     * Objects that will be used when the shaking is over and the prize won is revealed
                     */
                    const winTitleTextsContainer = this.createWinTitleText(pixiLibWrapper);
                    rootContainer.addChild(winTitleTextsContainer as any);
                    this.objects.winTitleTextsContainer = winTitleTextsContainer;

                    const winInfoTextsContainer = this.createWinInfoText(pixiLibWrapper);
                    rootContainer.addChild(winInfoTextsContainer as any);
                    this.objects.winInfoTextsContainer = winInfoTextsContainer;

                    const backToPlayButtonWrapper = this.createBackToPlayButton(assets.button, addDisposeAction, pixiLibWrapper);
                    rootContainer.addChild(backToPlayButtonWrapper as any);
                    this.objects.backToPlayButtonWrapper = backToPlayButtonWrapper;

                    pixiApp.stage.addChild(rootContainer as any);

                    /**
                     * Since this animation will be presented full screen there is no need to complicate the resize logic
                     * by computing the space inside the container and resizing according to that. we take the whole screen.
                     */
                    const onResizeHandler = () => { this.fitToBoundaries(pixiApp, rootContainer); }
                    onResizeHandler();
                    window.addEventListener("resize", onResizeHandler);

                    this.start(pixiLibWrapper);

                    return () => {
                        window.removeEventListener("resize", onResizeHandler);
                        // THERE IS A PROBLEM HERE BECAUSE DISPOSING A PIXI SPINE RESOURCE ALSO DISPOSES RAW ASSET AND WHEN THE PIXI APP ALSO ATTEMPS TO DESTROY ALL LOADED ASSETS IT THROWS AN ERROR BECAUSE IT IS TRYING TWICE
                        background.dispose();
                        ball.dispose();

                        // PRIZE BACKGROUND IS CREATED INSIDE THE BALL CREATE METHOD, FOR NOW WE DON'T HAVE ACCESS
                        // prizeBackground.dispose();

                        // THE BUTTONS WRAPPERS ARE CONTAINERS THAT KEEP THE SPINE ANIMATIONS FOR BUTTONS. NEED TO FIND A WAY TO DISPOSE THEM
                        // buttonShakeTheBallWrapper
                        // backToPlayButtonWrapper
                    };
                },
                dispose: () => {}
            },
            undefined, // canvasCss?: CSSProperties
            true // debugMode: boolean
        );

        return this.pixiAnimation.start();
    }

    attachToDOM(wrapperElement: HTMLElement) {
        this.pixiAnimation.attachToDOM(wrapperElement);
    }
    dispose() {
        this.pixiAnimation.dispose();
    }

    /** ///////////////////////////////////////////////////////////////////////////////////////
     ** ////////// ** ROOT CONTAINER ** ///////////////////////////////////////////////////////
     */ ///////////////////////////////////////////////////////////////////////////////////////
    private createRootContainer(pixiLibWrapper: IPixiLibWrapper) {
        const rootContainer = pixiLibWrapper.createContainer();
        rootContainer.name = "root";
        rootContainer.sortableChildren = true;
        return rootContainer;
    }


    /** ///////////////////////////////////////////////////////////////////////////////////////
     ** ////////// ** BACKGROUND ** ///////////////////////////////////////////////////////////
     */ ///////////////////////////////////////////////////////////////////////////////////////
    private createAndShowBackground(pixiAsset: PixiAsset, pixiLibWrapper: IPixiLibWrapper) {
        const background = pixiLibWrapper.createPixiSpineResource(pixiAsset);
    
        background.spineAnimation.x = -27;
        background.spineAnimation.y = -54;
        background.spineAnimation.zIndex = 10;
        background.spineAnimation.name = "background";
        background.spineAnimation.state.setAnimation(0, "static", false);
        return background;
    }


    /** ///////////////////////////////////////////////////////////////////////////////////////
     ** ////////// ** BLUE LAYER WITH A CIRCLE THAT SITS BETWEEN //////////////////////////////
     ** ////////// THE BACKGROUND+STARPARTICLES AND THE BALL ** ///////////////////////////////
     */ ///////////////////////////////////////////////////////////////////////////////////////
     private createAndShowBlueLayer(pixiAsset: PixiAsset, pixiLibWrapper: IPixiLibWrapper) {
        const bluelayer = pixiLibWrapper.createPixiSpriteResource(pixiAsset);
        bluelayer.sprite.x = -266;
        bluelayer.sprite.y = -50;
        bluelayer.sprite.scale.x = 0.5;
        bluelayer.sprite.scale.y = 0.5;
        bluelayer.sprite.zIndex = 15;
        bluelayer.sprite.name = "bluelayer";
        return bluelayer;
    }


    /** ///////////////////////////////////////////////////////////////////////////////////////
     ** ////////// ** BAll ** /////////////////////////////////////////////////////////////////
     */ ///////////////////////////////////////////////////////////////////////////////////////
    private createAndShowBall(ballPixiAsset: PixiAsset, transparentTextureBgPixiAsset: PixiAsset, pixiApp: Application<HTMLCanvasElement>, addDisposeAction: AddDisposeActionFn, pixiLibWrapper: IPixiLibWrapper) {
        const ball = pixiLibWrapper.createPixiSpineResource(ballPixiAsset);
        ball.spineAnimation.x = 360;
        ball.spineAnimation.y = 360;
        ball.spineAnimation.name = "ball";
        ball.spineAnimation.zIndex = 20;

        ball.spineAnimation.state.addListener({
            event: (entry, event) => {
                switch(event.data.name) {
                    // "mock_prize" event occurs every time a possible or the actual prize passed the center of the ball and it's not visible any more
                    // it gives us the opportunity to present another prize for when the container will pass the center of the ball again
                    case 'mock_prize': {
                        let prize;
                        // 'present_prize' is when the prizes are presented before the shake
                        if(this.prizePresentMode === 'present_prize') {
                            prize = this.prizes[this.currentPrizeIndex];
                        } else { // "mock_prize" is during the shake (when the prizes goes through the center of the ball)
                            prize = this.prizes[Math.floor(Math.random() * this.prizes.length)];
                        }
    
                        const container = pixiLibWrapper.createContainer();
                        const prizeBackground = pixiLibWrapper.createPixiSpriteResource(transparentTextureBgPixiAsset);
                        const prizeText = pixiLibWrapper.createText(prize.split(" ").join("\n"), { 
                            fontFamily: '888-Orbit',
                            fontWeight: '900',
                            align: 'center',
                            fontSize: 25,
                            fill: 0xffcf00,
                            lineHeight: 26
                        });
    
                        prizeText.x = prizeBackground.sprite.width / 2 - prizeText.getLocalBounds().width / 2;
                        prizeText.y = prizeBackground.sprite.height / 2 - prizeText.getLocalBounds().height / 2;
    
                        container.addChild(prizeBackground.sprite as any);
                        container.addChild(prizeText as any);
    
                        const winTextRenderTexture = pixiApp.renderer.generateTexture(container);
    
                        ball.spineAnimation.hackTextureBySlotName("amount", winTextRenderTexture, { width: container.width, height: container.height } as any);

                        // last but not least, add a disposable action so that prizeBackground will be cleaned after disposing the animation
                        addDisposeAction(() => { prizeBackground.dispose(); });
                        break;
                    }
                    // "prize" event occurs the actual prize will be presented to the user.
                    case 'prize': {
                        let prize = this.prizes[this.prizeWonIndex];
                        const container = pixiLibWrapper.createContainer();
                        const prizeBackground = pixiLibWrapper.createPixiSpriteResource(transparentTextureBgPixiAsset);
                        const prizeText = pixiLibWrapper.createText(prize.split(" ").join("\n"), { 
                            fontFamily: '888-Orbit',
                            fontWeight: '900',
                            align: 'center',
                            fontSize: 25,
                            fill: 0xffcf00,
                            lineHeight: 26
                        });
    
                        prizeText.x = prizeBackground.sprite.width / 2 - prizeText.getLocalBounds().width / 2;
                        prizeText.y = prizeBackground.sprite.height / 2 - prizeText.getLocalBounds().height / 2;
    
                        container.addChild(prizeBackground.sprite as any);
                        container.addChild(prizeText as any);
    
                        const winTextRenderTexture = pixiApp.renderer.generateTexture(container);
    
                        ball.spineAnimation.hackTextureBySlotName("amount", winTextRenderTexture, { width: container.width, height: container.height } as any);

                        // last but not least, add a disposable action so that prizeBackground will be cleaned after disposing the animation
                        addDisposeAction(() => { prizeBackground.dispose(); });

                        break;
                    }
                    case 'particle_win': {
                        this.playWinParticles();
                        break;
                    }
                }
            }
        });

        return ball;
    }
    private async playWinParticles() {
        await this.playSpineAnimation(this.objects.background, "animation_particle");
        await this.playSpineAnimation(this.objects.background, "animation_particle");
        await this.playSpineAnimation(this.objects.background, "static");
    }


    /** ///////////////////////////////////////////////////////////////////////////////////////
     ** ////////// ** PRIZE PRESENTATION TEXT (beneath the ball and above the prizes) ** //////
     */ ///////////////////////////////////////////////////////////////////////////////////////
    private createPrizePresentationText(pixiLibWrapper: IPixiLibWrapper) {
        const prizePresentationText = pixiLibWrapper.createText(
            this.services.translation.translate("and win amazing prizes:"),
            { fontFamily: '888-Orbit', fontWeight: '400', fontSize: 16, fill: 0xffffff }
        );
        prizePresentationText.alpha = 0;
        prizePresentationText.y = 680;
        prizePresentationText.zIndex = 20;
        prizePresentationText.name = "prizePresentationText";
        this.centerPixiTextToWidth(prizePresentationText, DESIGN_DIMENSIONS.WIDTH, 0);
        return prizePresentationText;
    }
    private async showPrizePresentationText() {
        await this.gsapService.animate(this.objects!.prizePresentationText, {alpha: 1, duration: 0.5});
    }
    private async hidePrizePresentationText() {
        await this.gsapService.animate(this.objects.prizePresentationText, {alpha: 0, duration: 0.5});
        this.objects!.prizePresentationText.visible = false;
    }


    /** ///////////////////////////////////////////////////////////////////////////////////////
     ** ////////// ** TITLE TEXTS (above the ball) ** /////////////////////////////////////////
     */ ///////////////////////////////////////////////////////////////////////////////////////
    private createTitleTexts(pixiLibWrapper: IPixiLibWrapper) {
        const titleText1 = pixiLibWrapper.createText(
            this.services.translation.translate("Shake the"),
            { fontFamily: '888-Orbit', fontWeight: '900', fontSize: 32, fill: 0xffffff }
        );
        const titleText2 = pixiLibWrapper.createText(
            this.services.translation.translate("Magic 8 Ball"),
            { fontFamily: '888-Orbit', fontWeight: '900', fontSize: 32, fill: 0xffcf00 }
        );
        const titleTextsContainer = pixiLibWrapper.createContainer();
        titleTextsContainer.x = 265;
        titleTextsContainer.y = 110;
        titleTextsContainer.alpha = 0;
        titleTextsContainer.addChild(titleText1 as any);
        titleTextsContainer.addChild(titleText2 as any);

        this.centerPixiTextToWidth(titleText1, DESIGN_DIMENSIONS.WIDTH, titleTextsContainer.x);
        this.centerPixiTextToWidth(titleText2, DESIGN_DIMENSIONS.WIDTH, titleTextsContainer.x);

        titleText2.y = 40;
        titleTextsContainer.zIndex = 21;

        return titleTextsContainer;
    }
    private async showTitleTexts() {
        await this.gsapService.animate(this.objects!.titleTexts, {alpha: 1, duration: 0.5});
    }
    private async hideTitleTexts() {
        await this.gsapService.animate(this.objects.titleTexts, {alpha: 0, duration: 0.70});
        this.objects.titleTexts.visible = false;
    }


    /** ///////////////////////////////////////////////////////////////////////////////////////
     ** ////////// ** PRIZE MASK ** ///////////////////////////////////////////////////////////
     ** ////////// ** (when they are presented they appear sliding down like appearing from behind something) 
     */ ///////////////////////////////////////////////////////////////////////////////////////
    private createPrizeMask(pixiLibWrapper: IPixiLibWrapper) {
        const prizesMask = pixiLibWrapper.createGraphics();
        prizesMask.beginFill(0xff0000);
        prizesMask.drawRect(0, 0, 740, 100);
        prizesMask.name = "prizesMask";
        prizesMask.y = 715;
        return prizesMask;
    }
    /** ///////////////////////////////////////////////////////////////////////////////////////
     ** ////////// ** PRIZE LAYER ** //////////////////////////////////////////////////////////
     ** ////////// ** (a Container that keeps prizes created with createPrize) ////////////////
     */ ///////////////////////////////////////////////////////////////////////////////////////
    private createPrizeLayer(prizes: string[], prizeAsset: PixiAsset, prizesMask: Graphics, pixiLibWrapper: IPixiLibWrapper) {
        const prizeObjects = prizes.map(prize => this.createPrize(prize, prizeAsset, pixiLibWrapper));
        const prizeLayer = pixiLibWrapper.createContainer();
        prizeLayer.name = "prizeLayer";
        prizeLayer.zIndex = 20;
        prizeLayer.x = 146;
        prizeLayer.y = 612;
        prizeLayer.mask = prizesMask;

        prizeObjects.forEach((prizeObject, i) => {
            prizeObject.x = (prizeObject.width * i) + (2 * i);
            prizeLayer.addChild(prizeObject as any);
        });

        return {
            layer: prizeLayer,
            objects: prizeObjects
        };
    }
    private createPrize(text: string, prizeAsset: PixiAsset, pixiLibWrapper: IPixiLibWrapper) {
        const prizeLayer = pixiLibWrapper.createContainer();
        const prizeBackground = pixiLibWrapper.createPixiSpriteResource(prizeAsset);

        prizeBackground.sprite.scale = {
            x: 0.2,
            y: 0.2
        }
        prizeLayer.addChild(prizeBackground.sprite as any);

        const prizeText = pixiLibWrapper.createText(
            text.split(" ").join("\n"),
            { fontFamily: '888-Orbit', fontWeight: '900', align: 'center', fontSize: 14, fill: 0xffcf00, lineHeight: 15 }
        );

        prizeText.x = prizeBackground.sprite.width / 2 - prizeText.getLocalBounds().width / 2;
        prizeText.y = prizeBackground.sprite.height / 2 - prizeText.getLocalBounds().height / 2;

        prizeLayer.addChild(prizeText as any);

        return prizeLayer;
    }
    private async hidePrizeLayer() {
        await this.gsapService.animate(this.objects.prizeWrapper.layer, {alpha: 0, duration: 0.5});
        this.objects!.prizeWrapper.layer.visible = false;
        this.objects!.prizesMask.visible = false;
    }


    /** ///////////////////////////////////////////////////////////////////////////////////////
     ** ////////// ** SHAKE THE BALL BUTTON ** ////////////////////////////////////////////////
     */ ///////////////////////////////////////////////////////////////////////////////////////
    private createShakeTheBallButton(buttonAsset: PixiAsset, addDisposeAction: AddDisposeActionFn, pixiLibWrapper: IPixiLibWrapper) {
        const button = pixiLibWrapper.createPixiSpineResource(buttonAsset);
        button.spineAnimation.name = "button";
        const buttonWrapper = pixiLibWrapper.createContainer();
        const buttonText = pixiLibWrapper.createText(
            this.services.translation.translate("SHAKE THE BALL!"),
            {
                fontFamily: '888-Orbit', fontWeight: '900', fontSize: 18, fill: 0xffcf00, lineHeight: 20,
            }
        );

        buttonWrapper.addChild(button.spineAnimation as any);
        buttonWrapper.addChild(buttonText as any);
    
        buttonText.x = -78;
        buttonText.y = -12;
        buttonWrapper.x = 360;
        buttonWrapper.y = 550;
    
        buttonWrapper.eventMode = "static";
        buttonWrapper.cursor = "pointer";
    
        buttonWrapper.on("pointerdown", this.startShakin);
    
        buttonWrapper.name = "buttonWrapper";
        buttonWrapper.zIndex = 100;
        buttonWrapper.alpha = 0;
        buttonWrapper.visible = false;

        // TODO: Check if it's better this line to be in showButton instead
        button.spineAnimation.state.setAnimation(0, "idle", true);
        
        // last but not least, add a disposable action so that button will be cleaned after disposing the animation
        addDisposeAction(() => { button.dispose(); });

        return buttonWrapper;
    }
    private async showShakeTheBallButton() {
        this.objects!.buttonShakeTheBallWrapper.visible = true;
        await this.gsapService.animate(this.objects!.buttonShakeTheBallWrapper, {alpha: 1, duration: 1});
    }
    private async hideShakeTheBallButton() {
        this.objects!.buttonShakeTheBallWrapper.off("pointerdown", this.startShakin);
        await this.gsapService.animate(this.objects!.buttonShakeTheBallWrapper, {alpha: 0, duration: 0.75});
        this.objects!.buttonShakeTheBallWrapper.visible = false;
    }


    /** ///////////////////////////////////////////////////////////////////////////////////////
     ** ////////// ** BACKGROUND PARTICLES (the stars that look slike snow) ** ////////////////
     */ ///////////////////////////////////////////////////////////////////////////////////////
    private createBackgroundParticles(backgroundParticlesAsset: PixiAsset, assets: PixiAsset[], pixiLibWrapper: IPixiLibWrapper) {
        const particleContainer = pixiLibWrapper.createParticleContainer();
        particleContainer.setProperties({
            scale: true,
            position: true,
            rotation: true,
            uvs: true,
            alpha: true,
        });
        particleContainer.zIndex = 11;
        const rawAssets = assets.map((a) => a.rawAsset);
        const newConfig = pixiLibWrapper.particleContainerUpgradeConfig(backgroundParticlesAsset.rawAsset, rawAssets);
        const emitter = pixiLibWrapper.createEmitter(particleContainer as any, newConfig);

        return {particleContainer, emitter};
    }
    private showBackgroundParticles() {
        this.objects!.bgParticlesEmitter.playOnceAndDestroy(() => {
            // console.log("createBackgroundParticles")
        });
        return Promise.resolve();
    }

    /** ///////////////////////////////////////////////////////////////////////////////////////
     ** ////////// ** WIN TITLE TEXT (above the ball) ** //////////////////////////////////////
     */ ///////////////////////////////////////////////////////////////////////////////////////
    private createWinTitleText(pixiLibWrapper: IPixiLibWrapper) {
        const winTitleTextsContainer = pixiLibWrapper.createContainer();
        const winTitleText1 = pixiLibWrapper.createText(
            this.services.translation.translate("Wow!"),
            { fontFamily: '888-Orbit', fontWeight: '900', fontSize: 32, fill: 0xffffff }
        );
        const localizedWinText = this.services.translation.translate(`You won {prizeWon}!`, {prizeWon: this.prizes[this.prizeWonIndex]});
        const winTitleText2 = pixiLibWrapper.createText(
            localizedWinText,
            { fontFamily: '888-Orbit', fontWeight: '900', fontSize: 32, fill: 0xffcf00 }
        );
        winTitleTextsContainer.x = 265;
        winTitleTextsContainer.y = 110;
        winTitleTextsContainer.alpha = 0;
        winTitleTextsContainer.addChild(winTitleText1 as any);
        winTitleTextsContainer.addChild(winTitleText2 as any);
        this.centerPixiTextToWidth(winTitleText1, DESIGN_DIMENSIONS.WIDTH, winTitleTextsContainer.x);
        this.centerPixiTextToWidth(winTitleText2, DESIGN_DIMENSIONS.WIDTH, winTitleTextsContainer.x);
        // winTitleText1.x = 20;
        winTitleText2.y = 40;
        winTitleTextsContainer.zIndex = 21;
        return winTitleTextsContainer;
    }
    private async showWinTitleText() {
        await this.gsapService.animate(this.objects.winTitleTextsContainer, {alpha: 1, duration: 0.5});
    }

    /** ///////////////////////////////////////////////////////////////////////////////////////
     ** ////////// ** WIN INFO TEXT (beneath the ball and above the prizes) ** ////////////////
     */ ///////////////////////////////////////////////////////////////////////////////////////
    private createWinInfoText(pixiLibWrapper: IPixiLibWrapper) {
        const winInfoTextsContainer = pixiLibWrapper.createContainer();

        const localizedWinText = this.services.translation.translate("{prizeWon} has been added to your balance.", {prizeWon: this.prizes[this.prizeWonIndex]});
        const winInfoText1 = pixiLibWrapper.createText(
            localizedWinText,
            { fontFamily: '888-Orbit', fontWeight: '900', fontSize: 16, fill: 0xffcf00 }
        );
        const winInfoText2 = pixiLibWrapper.createText(
            this.services.translation.translate("keep playing to get more\nrounds with shake-8"), 
            { fontFamily: '888-Orbit', fontWeight: '900', fontSize: 16, fill: 0xffffff, align: 'center' }
        );
        winInfoTextsContainer.y = 670;
        winInfoTextsContainer.alpha = 0;
        winInfoTextsContainer.addChild(winInfoText1 as any);
        winInfoTextsContainer.addChild(winInfoText2 as any);
        this.centerPixiTextToWidth(winInfoText1, DESIGN_DIMENSIONS.WIDTH, winInfoTextsContainer.x);
        this.centerPixiTextToWidth(winInfoText2, DESIGN_DIMENSIONS.WIDTH, winInfoTextsContainer.x);
        winInfoText2.y = 30;
        winInfoTextsContainer.zIndex = 21;
        return winInfoTextsContainer;
    }
    private async showWinInfoText() {
        await this.gsapService.animate(this.objects.winInfoTextsContainer, {alpha: 1, duration: 0.5});
    }

    /** ///////////////////////////////////////////////////////////////////////////////////////
     ** ////////// ** BACK TO PLAY BUTTON ** //////////////////////////////////////////////////
     */ ///////////////////////////////////////////////////////////////////////////////////////
    private createBackToPlayButton(buttonAsset: PixiAsset, addDisposeAction: AddDisposeActionFn, pixiLibWrapper: IPixiLibWrapper) {
        const backToPlayButtonWrapper = pixiLibWrapper.createContainer();
        backToPlayButtonWrapper.name = "backToPlayWrapper";
        const button = pixiLibWrapper.createPixiSpineResource(buttonAsset);
        button.spineAnimation.name = "backToPlaySpine";
        const buttonText = pixiLibWrapper.createText(
            this.services.translation.translate("BACK TO PLAY"),
            { fontFamily: '888-Orbit', fontWeight: '900', fontSize: 18, fill: 0xffcf00, lineHeight: 20 }
        );
        buttonText.name = "backToPlayText";

        backToPlayButtonWrapper.addChild(button.spineAnimation as any);
        backToPlayButtonWrapper.addChild(buttonText as any);

        buttonText.x = -70;
        buttonText.y = -11;
        backToPlayButtonWrapper.x = 360;
        backToPlayButtonWrapper.y = 550;

        backToPlayButtonWrapper.eventMode = "static";
        backToPlayButtonWrapper.cursor = "pointer";

        backToPlayButtonWrapper.on("pointerdown", () => {
            if (this.onPrizeClaim) {
                this.onPrizeClaim();
            }
        });

        backToPlayButtonWrapper.zIndex = 100;
        backToPlayButtonWrapper.alpha = 0;
        backToPlayButtonWrapper.visible = false;

        // TODO: Check if it's better this line to be in showButton instead
        button.spineAnimation.state.setAnimation(0, "idle", true);
        
        // last but not least, add a disposable action so that button will be cleaned after disposing the animation
        addDisposeAction(() => { button.dispose(); });

        return backToPlayButtonWrapper
    }
    private async showBackToPlayButton() {
        this.objects!.backToPlayButtonWrapper.visible = true;
        await this.gsapService.animate(this.objects.backToPlayButtonWrapper, {alpha: 1, duration: 1});
    }



    /** ///////////////////////////////////////////////////////////////////////////////////////
     ** ////////// ** In this section there are 2 utilitary methods used for comodity ** //////
     ** ////////// ** centerPixiTextToWidth, playSpineAnimation ** ////////////////////////////
     */ ///////////////////////////////////////////////////////////////////////////////////////
    private centerPixiTextToWidth(pixiText: Text, width: number, offset: number = 0) {
        const localBounds = pixiText.getLocalBounds();
        const textWidth = localBounds.width;
        pixiText.x = (width / 2 - textWidth / 2) - offset;
    }
    /**
     * Promifies the spine animation
     */
    private playSpineAnimation(pixiSpineResource: PixiSpineResource, animationName: string, loop = false): Promise<void> {
        return new Promise((resolve, reject) => {
            try {
                const completeListener = {
                    complete: () => {
                        pixiSpineResource.spineAnimation.state.removeListener(completeListener);
                        resolve();
                    }
                };
    
                pixiSpineResource.spineAnimation.state.addListener(completeListener)
        
                pixiSpineResource.spineAnimation.state.setAnimation(0, animationName, loop);
            } catch (e) {
                reject(e);
            }
        });
    };

    /** ///////////////////////////////////////////////////////////////////////////////////////
     ** ////////// ** adaptSize is the handler for resizing theanimation ** ///////////////////
     */ ///////////////////////////////////////////////////////////////////////////////////////
    private fitToBoundaries(pixiApp: Application<HTMLCanvasElement>, rootContainer: Container) {
        const designRatio = DESIGN_DIMENSIONS.WIDTH / DESIGN_DIMENSIONS.HEIGHT;
        let targetWidth = 0, targetHeight = 0;

        if (this.services.mediaQuery.lAndAbove) {
            // width is larger than 1024 and we can render the normal PC
            // at this size, the device most probably is a desktop or a tablet (doesnt matter if in landscape or in portrait) 
            const viewportWidth = window.innerWidth * 0.9;
            const viewportHeight = window.innerHeight * 0.9;

            const viewportRatio = viewportWidth / viewportHeight;
            if (viewportRatio > designRatio) {
                // the viewport is wider so the height must be taken as maximum and the width must be computed to respect the design ratio
                targetHeight = viewportHeight;
                targetWidth = targetHeight * designRatio;
            } else {
                // the viewport is narrower so the width must be taken as maximum and the height must be computed to respect the design ratio
                targetWidth = viewportWidth;
                targetHeight = targetWidth / designRatio;
            }
        } else if (this.services.mediaQuery.portrait) {
            // small devices like tablets in portrait mode and phones in portrait mode
            // we can render the animation fullscreen in mobile mode 
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            targetWidth = viewportWidth;
            targetHeight = viewportHeight;
        } else {
            // small devices like tablets or phones in landscape mode
            // TODO: Show warning
        }

        // Because mobiles have tall screens, the device ratio (width/height) is smaller than the design ratio.
        // So we scale the animation to fit the screen height, and the width of the animation will be bigger than the screen so we just center it
        // On PC, scaleY and scaleX have the same value because the animation is shown to scale so no need to compute both
        // let scaleX = targetWidth / DESIGN_DIMENSIONS.WIDTH;
        let scaleY = targetHeight / DESIGN_DIMENSIONS.HEIGHT;

        pixiApp.renderer.resize(targetWidth, targetHeight);

        const canvas = pixiApp.view;
        canvas.style.width = pixiApp.renderer.width + "px";
        canvas.style.height = pixiApp.renderer.height  + "px";

        // this formula will return 0 always on PC because the canvas animation will respect the design ratio
        rootContainer.x = ((targetWidth - DESIGN_DIMENSIONS.WIDTH * scaleY) / 2);
        rootContainer.scale = {
            x: scaleY,
            y: scaleY
        }

        if (this.onResize) {
            this.onResize(scaleY);
        }
    }


    private async presentPrize(remainingPrizesToPresent: string[], pixiLibWrapper: IPixiLibWrapper) {
        if (remainingPrizesToPresent && remainingPrizesToPresent.length > 0) {
            this.currentPrizeIndex = this.prizes.length - remainingPrizesToPresent.length;
            remainingPrizesToPresent.shift();
            const currentPrizeObject = this.objects!.prizeWrapper.objects[this.currentPrizeIndex];

            this.gsapService.animate(currentPrizeObject, {y: 100, duration: 0.5});
            await this.playSpineAnimation(this.objects.ball, "present_prize");
            await this.presentPrize(remainingPrizesToPresent, pixiLibWrapper);
        } else {
            await this.playSpineAnimation(this.objects.ball, "out_intro");
        }
    }

    private async start(pixiLibWrapper: IPixiLibWrapper) {
        await this.playSpineAnimation(this.objects!.ball, "static");
        // after, show the background particles
        this.showBackgroundParticles();
        // first show the ball and play the animation with the number 8 filling up
        await this.playSpineAnimation(this.objects!.ball, "intro");
        // after, present the texts "Shake the Magic 8 Ball" "and win amazing prizes"
        this.showTitleTexts();
        await this.showPrizePresentationText()
        // after, present the prizes one by one both in the center of the ball and down
        const prizesToPresent = structuredClone(this.prizes);
        await this.presentPrize(prizesToPresent, pixiLibWrapper);
        // after, show shake the ball button
        await this.showShakeTheBallButton();
    }

    private startShakin = async () => {
        if (this.onShakeStart) { this.onShakeStart(); }
        this.prizePresentMode = "mock_prize";
        this.hideTitleTexts();
        await this.hideShakeTheBallButton();
        await this.playSpineAnimation(this.objects!.ball, "start");
        await this.playSpineAnimation(this.objects!.ball, "win");
        this.hidePrizeLayer();
        this.hidePrizePresentationText();
        this.showWinTitleText();
        this.showWinInfoText();
        if (this.onShakeEnd) { this.onShakeEnd(); }
        await this.showBackToPlayButton();
    }
}
